import { useEffect, useMemo, useState } from 'react';
import { applyActionCode } from 'firebase/auth';
import { auth } from '../firebase';

export default function AuthActionHandler() {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState<string>('処理しています…');

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const mode = params.get('mode') || '';
  const oobCode = params.get('oobCode') || '';
  // 招待されたスタッフのパスワード設定からの遷移。
  // Firebase が続行時に付けるパラメータは状況によって変わるため、
  // リンク生成側で必ず flow=staff-setup を付けて用途を明示している。
  const flow = params.get('flow') || '';
  const isPasswordSetup = flow === 'staff-setup' || mode === 'resetPassword';
  // ここに来た時点でパスワード設定は完了している（Firebase 側の画面で処理済み）。
  // このページはアプリへ戻す導線を出すだけ。
  const appUrl = 'lessonmate://';
  
  // 環境変数からホームURLを取得（デフォルトは本番環境）
  const env = (import.meta as any).env || (window as any).__ENV__ || {};
  const projectId = env.VITE_FIREBASE_PROJECT_ID || 'lessonmate-d603d';
  const homeUrl = `https://${projectId}.web.app/`;

  useEffect(() => {
    (async () => {
      try {
        // このページはメールアドレス変更用だが、他の操作の継続先として
        // 遷移してくることがある。その場合 oobCode は既に消費済みで、
        // applyActionCode を呼ぶと「変更に失敗しました」と誤解を招く表示になる。
        // mode を見て、扱える操作のときだけ適用する。
        if (isPasswordSetup) {
          setStatus('success');
          setMessage('');
          return;
        }
        if (mode && mode !== 'verifyAndChangeEmail' && mode !== 'verifyEmail') {
          setStatus('success');
          setMessage('処理が完了しました。');
          return;
        }
        if (!oobCode) {
          throw new Error('invalid oobCode');
        }
        // メールアドレス変更（verifyAndChangeEmail）を適用
        await applyActionCode(auth, oobCode);
        setStatus('success');
        setMessage('メールアドレスの変更が完了しました');
        // 数秒後にアプリへディープリンクで戻す
        const timer = setTimeout(() => {
          window.location.href = 'lessonmate://email-changed';
        }, 1000);
        return () => clearTimeout(timer);
      } catch (e) {
        console.error('applyActionCode failed:', e);
        setStatus('error');
        setMessage('メールアドレスの変更に失敗しました。リンクの有効期限切れや既に処理済みの可能性があります。');
      }
    })();
  }, [oobCode, mode, isPasswordSetup]);

  return (
    <div style={styles.container as any}>
      <div style={styles.card as any}>
        <div style={styles.header as any}>
          <img src="/favicon.svg" alt="LessonMate" style={{ width: 36, height: 36, marginRight: 8 }} />
          <h2 style={{ margin: 0 }}>LessonMate</h2>
        </div>
        <div style={{ marginTop: 16 }}>
          {status === 'processing' && <p>{message}</p>}
          {status === 'success' && isPasswordSetup && (
            <>
              <h3 style={{ marginTop: 0 }}>パスワードを設定しました</h3>
              <p>LessonMate アプリを開いて、設定したパスワードでログインしてください。</p>
              <a href={appUrl} style={styles.button as any}>アプリを開く</a>
              <p style={styles.hint as any}>
                このボタンで開かない場合は、ホーム画面から LessonMate を起動してください。
              </p>
            </>
          )}
          {status === 'success' && !isPasswordSetup && (
            <>
              <h3 style={{ marginTop: 0 }}>メールアドレスが更新されました</h3>
              <p>アプリに戻って処理を続けられます。</p>
              <a href="lessonmate://email-changed" style={styles.button as any}>アプリに戻る</a>
            </>
          )}
          {status === 'error' && (
            <>
              <h3 style={{ marginTop: 0 }}>処理に失敗しました</h3>
              <p>{message}</p>
              <a href="lessonmate://email-changed" style={styles.button as any}>アプリに戻る</a>
            </>
          )}
        </div>
        <div style={styles.footer as any}>
          <a href={homeUrl} style={{ color: '#1976d2', textDecoration: 'none' }}>LessonMate ホーム</a>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    padding: 16,
  },
  card: {
    width: '100%',
    maxWidth: 520,
    backgroundColor: '#fff',
    borderRadius: 12,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    padding: 20,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
  },
  button: {
    display: 'inline-block',
    marginTop: 12,
    backgroundColor: '#1976d2',
    color: '#fff',
    padding: '10px 16px',
    borderRadius: 8,
    textDecoration: 'none',
  },
  hint: {
    marginTop: 12,
    color: '#888',
    fontSize: 13,
  },
  footer: {
    marginTop: 24,
    textAlign: 'right',
    color: '#888',
    fontSize: 12,
  },
};


